// Kaycoo Agentic Platform API client
// Backend host is configured via VITE_CLAUDE_CD_API_URL (see src/lib/constants.ts)
// Architecture: FastAPI backend with server-side AI execution (proxy pattern)
// Authentication: Application API keys (X-API-Key header) via external API
// Endpoints: /api/v1/external/* — auto-scoped by API key, no app_id param needed

import { CLAUDE_CD_API_URL } from '@/lib/constants'

const BACKEND_URL = CLAUDE_CD_API_URL

function getOrganizationApiKey(): string | null {
  // API key from build-time env var, with hardcoded fallback for the D365
  // application. Never read from localStorage — a stale cached key caused
  // 401s after the platform's auth overhaul.
  return import.meta.env.VITE_KAYCOO_API_KEY || 'kap_d365_d2d576a7fc5846feadfa8886e26869de'
}

function buildHeaders(): HeadersInit {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  }

  const apiKey = getOrganizationApiKey()
  if (apiKey) {
    headers['X-API-Key'] = apiKey
  }

  return headers
}

export interface Agent {
  assistant_id: string
  agent_id?: string
  name: string
  description: string
  model_id: string
  provider: string
  system_prompt: string
  temperature: number
  max_tokens: number
  status: string
  capabilities: unknown
  tools: unknown
  agent_type?: string
  application_id?: string
  created_at: string
  updated_at: string
}

export interface Provider {
  provider_id: string | null
  name: string
  provider_type: string
  status: string
  requires_api_key: boolean
}

export interface AgentExecutionRequest {
  input: string
  api_key?: string
}

export interface AgentExecutionResponse {
  success?: boolean
  response?: string
  result?: string
  output?: string
  execution_time?: number
  tokens?: number
  model?: string
  provider?: string
  agent_id?: string
  error?: string
}

/**
 * Fetch agents from Claude-CD backend, filtered by application_id.
 * Backend filters on query param; frontend also filters as fallback.
 */
export async function fetchAgents(): Promise<Agent[]> {
  // Uses the external API which auto-scopes by the X-API-Key's application.
  // No app_id query param or client-side filtering needed.
  const response = await fetch(`${BACKEND_URL}/api/v1/external/agents`, {
    method: 'GET',
    headers: buildHeaders(),
  })

  if (!response.ok) {
    console.error(`Failed to fetch agents: ${response.status}`)
    return []
  }

  const data = await response.json()

  let allAgents: Agent[] = []
  if (Array.isArray(data)) {
    allAgents = data
  } else if (data.data && Array.isArray(data.data)) {
    allAgents = data.data
  }

  // Normalize agent IDs
  allAgents.forEach((agent) => {
    if (!agent.agent_id && agent.assistant_id) {
      agent.agent_id = agent.assistant_id
    }
  })

  return allAgents
}

export async function fetchProviders(): Promise<Provider[]> {
  const response = await fetch(`${BACKEND_URL}/api/v1/providers`, {
    method: 'GET',
    headers: buildHeaders(),
  })

  if (!response.ok) {
    throw new Error(`Failed to fetch providers: ${response.status}`)
  }

  const data = await response.json()

  if (Array.isArray(data)) return data
  if (data.data && Array.isArray(data.data)) return data.data
  if (data.providers && Array.isArray(data.providers)) return data.providers
  return []
}

/**
 * Execute an agent via the backend.
 * Pass api_key in the request for providers that require one (CPC pattern).
 */
export async function executeAgent(
  agentId: string,
  request: AgentExecutionRequest
): Promise<AgentExecutionResponse> {
  console.log('[agent-client] Executing agent:', agentId)
  console.log('[agent-client] Request:', request)
  console.log('[agent-client] URL:', `${BACKEND_URL}/api/v1/external/agents/${agentId}/execute`)

  const response = await fetch(`${BACKEND_URL}/api/v1/external/agents/${agentId}/execute`, {
    method: 'POST',
    headers: buildHeaders(),
    body: JSON.stringify(request),
  })

  console.log('[agent-client] Response status:', response.status)

  const raw = await response.json()
  console.log('[agent-client] Raw response:', raw)

  if (!response.ok) {
    console.error('[agent-client] Error response:', raw)
    throw new Error(raw.detail || raw.error || 'Failed to execute agent')
  }

  // Unwrap nested response: Kaycoo returns { status: 'ok', data: { response: '...', ... } }
  const data = (raw.data && typeof raw.data === 'object' && !Array.isArray(raw.data))
    ? raw.data
    : raw

  console.log('[agent-client] Unwrapped data:', data)

  // Normalize response fields — Kaycoo platform returns text in 'output'
  const text = data.response || data.result || data.output || ''
  if (text) {
    data.response = text
    data.result = text
  }

  console.log('[agent-client] Final normalized data:', data)

  return data
}

export async function testBackendConnection(): Promise<boolean> {
  try {
    const response = await fetch(`${BACKEND_URL}/health`, {
      method: 'GET',
      headers: buildHeaders(),
    })
    return response.ok
  } catch {
    return false
  }
}

export function getBackendUrl(): string {
  return BACKEND_URL
}
